// Firmas · emitir sin pago (0122, pedido de Rodrigo, 2026-10-27): lo que la caja pidió emitir sin su
// pago, para autorizarlo o rechazarlo (comentario opcional, nunca un motivo obligatorio), y debajo el
// historial para tesorería: quién pidió, quién firmó, cuándo y qué documento salió. El backend dice
// por fila si quien mira puede firmar y lo vuelve a exigir (segregación: no firma quien pidió).
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import type { FilaSinPago } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fechaHora, NOMBRE_CONDICION, pesos, rut } from '../comun/formato';
import { presentarError } from '../errores/catalogo';
import { useSesion } from '../sesion/SesionContexto';
import { useFirmasSinPago } from './SinPagoMeson';

const IMPEDIMENTOS: Record<string, string> = {
  FIRMA_REQUIERE_PERSONA: 'La firma la hace una persona con sesión',
  APROBADOR_NO_ACTIVO: 'Tu usuario no está activo',
  FIRMANTE_SIN_PERMISO_DEL_AMBITO: 'No tienes el permiso para autorizar documentos sin pago',
  AUTOAPROBACION_PROHIBIDA: 'La pediste tú: la autoriza otra persona',
};
const ESTADO: Record<string, string> = {
  PENDIENTE: 'Pendiente',
  APROBADA: 'Autorizada (sin emitir)',
  RECHAZADA: 'Rechazada',
  RETIRADA: 'Retirada',
  USADA: 'Emitida sin pago',
};
const DOC: Record<string, string> = { BOLETA: 'Boleta', FACTURA: 'Factura', VALE: 'Vale' };

function emitido(f: FilaSinPago): string {
  const e = f.emitido;
  if (!e) return DOC[f.documento] ?? f.documento;
  if (e.valeNumero) return `Vale N° ${e.valeNumero}`;
  const nombre = e.tipoDte === 33 ? 'Factura' : e.tipoDte === 39 ? 'Boleta' : 'Documento';
  return e.folio ? `${nombre} N° ${e.folio}` : `${nombre} (folio en curso)`;
}

/** Hace 30 días (AAAA-MM-DD), para el historial. */
function hace30Dias(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

export function FirmasSinPago({
  alAbrirNota,
}: {
  alAbrirNota: (notaId: string, numero: string) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const [desde] = useState(hace30Dias);
  const q = useFirmasSinPago(empresaId, { desde });
  const filas = q.data?.items ?? [];
  const pendientes = filas.filter((f) => f.estado === 'PENDIENTE');
  const resueltas = filas.filter((f) => f.estado !== 'PENDIENTE');
  const [decision, setDecision] = useState<{
    f: FilaSinPago;
    accion: 'aprobar' | 'rechazar';
  } | null>(null);
  const [comentario, setComentario] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolver = async () => {
    if (!decision) return;
    setProcesando(true);
    setError(null);
    try {
      await api.post(
        `/ventas/sin-pago/${decision.f.id}/${decision.accion}`,
        {
          version: decision.f.version,
          ...(comentario.trim() ? { comentario: comentario.trim() } : {}),
        },
        { idempotencia: crypto.randomUUID() },
      );
      setDecision(null);
      setComentario('');
    } catch (e) {
      const p = presentarError(e);
      setError(p.mensaje ? `${p.titulo}: ${p.mensaje}` : p.titulo);
    } finally {
      setProcesando(false);
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'ventas', 'sin-pago'] });
    }
  };

  return (
    <div className="firmas-sin-pago" data-firmas-sin-pago>
      <h2>Emitir sin pago · pendientes</h2>
      {q.isError && <PanelError error={q.error} />}
      <table className="grilla compacta" aria-label="Emitir sin pago pendientes">
        <thead>
          <tr>
            <th>Nota</th>
            <th>Cliente</th>
            <th>Documento</th>
            <th className="num">Monto sin pago</th>
            <th>Condición</th>
            <th>Pidió</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {pendientes.length === 0 && (
            <tr>
              <td colSpan={7} className="apagado">
                Nada esperando autorización para emitir sin pago
              </td>
            </tr>
          )}
          {pendientes.map((f) => (
            <tr key={f.id} data-sin-pago={f.id}>
              <td>
                <button
                  type="button"
                  className="enlace mono"
                  onClick={() => alAbrirNota(f.notaId, f.numeroVisible)}
                >
                  {f.numeroVisible}
                </button>
              </td>
              <td>
                {f.cliente}
                <div className="apagado mono">{rut(f.clienteRut)}</div>
              </td>
              <td>{DOC[f.documento] ?? f.documento}</td>
              <td className="num">{pesos(f.monto)}</td>
              <td>{NOMBRE_CONDICION[f.condicionPago] ?? f.condicionPago}</td>
              <td>
                {f.solicitadaPorNombre ?? '—'}
                <div className="apagado">{fechaHora(f.solicitadaEn)}</div>
              </td>
              <td>
                {f.puedeFirmar ? (
                  <>
                    <button
                      type="button"
                      className="primario"
                      onClick={() => setDecision({ f, accion: 'aprobar' })}
                    >
                      Autorizar
                    </button>{' '}
                    <button type="button" onClick={() => setDecision({ f, accion: 'rechazar' })}>
                      Rechazar
                    </button>
                  </>
                ) : (
                  <span className="apagado" data-impedimento={f.impedimento}>
                    {IMPEDIMENTOS[f.impedimento ?? ''] ?? f.impedimento}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Emitir sin pago · historial (30 días)</h2>
      <table className="grilla compacta" aria-label="Emitir sin pago historial">
        <thead>
          <tr>
            <th>Nota</th>
            <th>Cliente</th>
            <th className="num">Monto sin pago</th>
            <th>Estado</th>
            <th>Documento</th>
            <th>Pidió</th>
            <th>Resolvió</th>
            <th>Comentario</th>
          </tr>
        </thead>
        <tbody>
          {resueltas.length === 0 && (
            <tr>
              <td colSpan={8} className="apagado">
                Sin autorizaciones en los últimos 30 días
              </td>
            </tr>
          )}
          {resueltas.map((f) => (
            <tr key={f.id} data-sin-pago={f.id} data-estado={f.estado}>
              <td>
                <button
                  type="button"
                  className="enlace mono"
                  onClick={() => alAbrirNota(f.notaId, f.numeroVisible)}
                >
                  {f.numeroVisible}
                </button>
              </td>
              <td>{f.cliente}</td>
              <td className="num">{pesos(f.monto)}</td>
              <td>{ESTADO[f.estado] ?? f.estado}</td>
              <td>{emitido(f)}</td>
              <td>
                {f.solicitadaPorNombre ?? '—'}
                <div className="apagado">{fechaHora(f.solicitadaEn)}</div>
              </td>
              <td>
                {f.resueltaPorNombre ?? '—'}
                <div className="apagado">{fechaHora(f.resueltaEn)}</div>
              </td>
              <td>{f.comentario ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {decision && (
        <Modal
          titulo={
            decision.accion === 'aprobar'
              ? `Autorizar emitir sin pago · ${decision.f.numeroVisible}`
              : `Rechazar emitir sin pago · ${decision.f.numeroVisible}`
          }
          ancho="angosto"
          alCerrar={() => !procesando && setDecision(null)}
          pie={
            <>
              <button type="button" onClick={() => setDecision(null)} disabled={procesando}>
                Cancelar
              </button>
              <button
                type="button"
                className="primario"
                data-autofoco
                disabled={procesando}
                onClick={() => void resolver()}
              >
                {procesando
                  ? 'Guardando…'
                  : decision.accion === 'aprobar'
                    ? 'Autorizar'
                    : 'Rechazar'}
              </button>
            </>
          }
        >
          <p>
            {DOC[decision.f.documento] ?? 'Documento'} de {decision.f.cliente} por{' '}
            <b>{pesos(decision.f.monto)}</b> sin pago, pedido por{' '}
            {decision.f.solicitadaPorNombre ?? 'la caja'}.
          </p>
          <label className="campo">
            <span className="etiqueta">Comentario (opcional)</span>
            <input
              type="text"
              aria-label="Comentario"
              maxLength={500}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
            />
          </label>
          <p className="ayuda">Queda registrado que tú lo resolviste, con fecha y hora.</p>
          {error && <p className="alerta">{error}</p>}
        </Modal>
      )}
    </div>
  );
}
